import React, { useEffect, useMemo, useState } from "react";
import AppShell from "../components/AppShell";
import { PlayerTag } from "../components/badges";
import RangeFilter, { passesRange } from "../components/RangeFilter";
import VoteButtons from "../components/VoteButtons";
import { toast } from "sonner";
import {
    Sparkle,
    ArrowsDownUp,
    Bandaids,
    Warning,
    Info,
} from "@phosphor-icons/react";
import { getHiddenGems } from "../lib/hidden-gems-api";
import { track } from "../lib/analytics";

const FDR_COLOR = {
    1: "#9EE8B4",
    2: "#9DB7F5",
    3: "#FFF3C2",
    4: "#FF8800",
    5: "#FF2A2A",
};

const CONFIDENCE_STYLE = {
    HIGH: { bg: "#9EE8B4", text: "#050505" },
    MEDIUM: { bg: "#FFF3C2", text: "#050505" },
    LOW: { bg: "#6B7280", text: "#ffffff" },
    LOW_SAMPLE: { bg: "#6B7280", text: "#ffffff" },
};

const MATCHUP_COLOR = {
    FAVOURITE_OPPONENT: "#A855F7",
    BAD_MATCHUP: "#FF2A2A",
    LOW_SAMPLE: "#6B7280",
    NEUTRAL: "#A1A1AA",
};

export default function HiddenGems() {
    const [data, setData] = useState({ gameweek: null, gems: [], weights: {} });
    const [loading, setLoading] = useState(true);
    const [ownershipRange, setOwnershipRange] = useState({ selectedMin: 0, selectedMax: 15 });
    const [poolSize, setPoolSize] = useState(60);
    const [topN, setTopN] = useState(30);
    const [expandedId, setExpandedId] = useState(null);

    useEffect(() => { track("tool_open", { tool: "hidden" }); }, []);

    const load = async () => {
        setLoading(true);
        try {
            const d = await getHiddenGems({
                maxOwnership: ownershipRange.selectedMax,
                poolSize,
                topN,
            });
            setData(d);
        } catch (e) {
            console.warn("Hidden gems load failed:", e?.response?.data?.detail || e?.message);
            toast.error("Failed to load hidden gems");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Client-side range filter on top of the API response (which already applied
    // selectedMax via maxOwnership). selectedMin filtering happens here.
    const filteredGems = useMemo(
        () => data.gems.filter((g) => passesRange(g.ownership, {
            isEnabled: true,
            selectedMin: ownershipRange.selectedMin,
            selectedMax: ownershipRange.selectedMax,
        })),
        [data.gems, ownershipRange.selectedMin, ownershipRange.selectedMax],
    );

    const withMatchupTag = useMemo(
        () => filteredGems.filter((g) => g.tags.includes("FAVOURITE_OPPONENT")).length,
        [filteredGems],
    );

    return (
        <AppShell liveContext={data.gameweek ? { gameweek: data.gameweek } : null}>
            <div className="space-y-6">
                <header className="flex flex-wrap items-end justify-between gap-3">
                    <div>
                        <h1 className="font-display font-black text-3xl md:text-4xl uppercase tracking-tighter flex items-center gap-2">
                            <Sparkle size={28} weight="fill" className="text-fa-yellow" />
                            Hidden Gems
                        </h1>
                        <p className="text-white/60 text-sm mt-1 max-w-2xl">
                            Low-owned players scored on form, fixtures, historical matchup, threat, minutes
                            and ownership opportunity. History sourced from Fantasy Nutmeg (2016/17→).
                        </p>
                    </div>
                    <div className="flex items-center gap-2" data-testid="gems-weights-legend">
                        {Object.entries(data.weights || {}).map(([k, v]) => (
                            <span
                                key={k}
                                className="font-mono text-[10px] uppercase tracking-widest text-white/50"
                            >
                                {k} {Math.round(v * 100)}%
                            </span>
                        ))}
                    </div>
                </header>

                <section className="bg-[#121212] border border-white/10 p-4" data-testid="gems-controls">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                        <div className="md:col-span-2">
                            <RangeFilter
                                id="ownership"
                                label="Ownership"
                                minValue={0}
                                maxValue={50}
                                selectedMin={ownershipRange.selectedMin}
                                selectedMax={ownershipRange.selectedMax}
                                step={0.5}
                                unit="%"
                                format="percent"
                                onChange={setOwnershipRange}
                                defaults={{ selectedMin: 0, selectedMax: 15 }}
                                testid="range-ownership"
                            />
                        </div>
                        <ControlBlock label={`Pool size ${poolSize}`}>
                            <input
                                type="range"
                                min={20}
                                max={120}
                                step={10}
                                value={poolSize}
                                onChange={(e) => setPoolSize(Number(e.target.value))}
                                className="w-full accent-fa-yellow"
                                data-testid="control-pool-size"
                            />
                        </ControlBlock>
                        <ControlBlock label={`Show top ${topN}`}>
                            <input
                                type="range"
                                min={5}
                                max={60}
                                step={5}
                                value={topN}
                                onChange={(e) => setTopN(Number(e.target.value))}
                                className="w-full accent-fa-yellow"
                                data-testid="control-top-n"
                            />
                        </ControlBlock>
                        <button
                            onClick={load}
                            disabled={loading}
                            className="font-mono text-xs uppercase tracking-widest bg-fa-yellow text-black px-4 py-2 hover:bg-[#CBE815] disabled:opacity-60"
                            data-testid="run-scan-btn"
                        >
                            {loading ? "Scanning…" : "Run scan"}
                        </button>
                    </div>
                    <div className="mt-3 font-mono text-[10px] uppercase tracking-widest text-white/50 flex items-center gap-4">
                        <span data-testid="gems-count">{filteredGems.length} of {data.gems.length} gems shown</span>
                        <span data-testid="favourite-opponent-count">
                            {withMatchupTag} with favourite opponent
                        </span>
                    </div>
                </section>

                {loading ? (
                    <div className="text-white/40 font-mono text-sm">
                        Scanning the market — fetching historical data from Fantasy Nutmeg…
                    </div>
                ) : filteredGems.length === 0 ? (
                    <div className="border border-dashed border-white/10 p-10 text-center text-white/50 font-mono text-xs uppercase">
                        No gems matched these filters. Loosen the ownership range or run a fresh scan.
                    </div>
                ) : (
                    <div className="space-y-2" data-testid="gems-list">
                        {filteredGems.map((g, idx) => (
                            <GemCard
                                key={g.id}
                                g={g}
                                rank={idx + 1}
                                expanded={expandedId === g.id}
                                onToggle={() => setExpandedId(expandedId === g.id ? null : g.id)}
                                gameweek={data.gameweek}
                            />
                        ))}
                    </div>
                )}
            </div>
        </AppShell>
    );
}

function ControlBlock({ label, children }) {
    return (
        <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-white/50 mb-1.5">
                {label}
            </div>
            {children}
        </div>
    );
}

function GemCard({ g, rank, expanded, onToggle, gameweek }) {
    const bm = g.historical_best_matchup;
    const hasFav = g.tags.includes("FAVOURITE_OPPONENT");
    const hasBad = g.tags.includes("BAD_MATCHUP");
    const accentColor = hasFav ? "#A855F7" : hasBad ? "#FF2A2A" : "#FFF3C2";
    const scorePct = Math.round(g.hidden_gem_score * 100);

    return (
        <div
            className="bg-[#121212] border border-white/10 hover:border-white/30 transition-colors"
            data-testid={`gem-card-${g.id}`}
        >
            <div
                role="button"
                tabIndex={0}
                onClick={onToggle}
                onKeyDown={(e) => (e.key === "Enter" ? onToggle() : null)}
                className="cursor-pointer p-4 flex flex-wrap items-center gap-x-4 gap-y-2"
            >
                <span
                    className="font-display font-black uppercase text-2xl tabular-nums leading-none w-10 text-right"
                    style={{ color: accentColor }}
                >
                    {String(rank).padStart(2, "0")}
                </span>
                <div className="flex flex-col min-w-[160px]">
                    <div className="flex items-center gap-2">
                        <span className="font-display font-bold uppercase text-lg leading-none">
                            {g.name}
                        </span>
                        <span className="font-mono text-[10px] text-white/50">
                            {g.team} · {g.position}
                        </span>
                    </div>
                    <div className="font-mono text-xs text-fa-yellow mt-1">
                        £{g.price.toFixed(1)}m · {g.ownership}% owned · form {g.form.toFixed(1)}
                    </div>
                </div>
                <div className="flex-1" />
                <div className="flex items-center gap-2 flex-wrap">
                    {g.tags.map((t) => (
                        <PlayerTag key={t} tag={t} />
                    ))}
                </div>
                <VoteButtons
                    playerId={g.id}
                    gwWindow={3}
                    votes={g.community_votes}
                    predictedAtCast={g.hidden_gem_score * 10}
                    targetGwStart={gameweek}
                />
                <div className="flex items-center gap-2 ml-auto">
                    {bm && (
                        <div
                            className="font-mono text-[10px] uppercase tracking-widest border px-2 py-1 flex items-center gap-1"
                            style={{ color: MATCHUP_COLOR[bm.label] || "#A1A1AA", borderColor: `${MATCHUP_COLOR[bm.label] || "#A1A1AA"}60` }}
                            title={`vs ${bm.opponent} over ${bm.matches} matches — Δ ${bm.matchup_delta >= 0 ? "+" : ""}${bm.matchup_delta} ppm (${bm.confidence})`}
                            data-testid={`best-matchup-${g.id}`}
                        >
                            <ArrowsDownUp size={10} weight="bold" />
                            vs {bm.opponent} Δ{bm.matchup_delta >= 0 ? "+" : ""}{bm.matchup_delta}
                        </div>
                    )}
                    <ScoreMeter score={scorePct} color={accentColor} />
                </div>
            </div>
            {expanded && <GemDetails g={g} />}
        </div>
    );
}

function ScoreMeter({ score, color }) {
    return (
        <div
            className="font-mono text-[10px] uppercase tracking-widest border px-2 py-1 flex items-center gap-2"
            style={{ color, borderColor: `${color}60` }}
        >
            <span className="font-display font-black text-base leading-none">{score}</span>
            <span className="opacity-60">score</span>
        </div>
    );
}

function GemDetails({ g }) {
    const sb = g.score_breakdown;
    return (
        <div className="border-t border-white/10 p-4 bg-black/40 space-y-4">
            <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-2">
                    Score breakdown
                </div>
                <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                    <MeterBar label="Form" value={sb.form} weight={0.25} />
                    <MeterBar label="Fixture" value={sb.fixture} weight={0.25} />
                    <MeterBar label="Historical" value={sb.historical} weight={0.20} />
                    <MeterBar label="Threat" value={sb.threat} weight={0.15} />
                    <MeterBar label="Minutes" value={sb.minutes} weight={0.10} />
                    <MeterBar label="Ownership" value={sb.ownership} weight={0.05} />
                </div>
            </div>

            <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-2">
                    Next 3 fixtures — historical matchup
                </div>
                <div className="flex flex-wrap gap-2" data-testid={`matchups-${g.id}`}>
                    {(g.all_matchups || []).map((m) => (
                        <MatchupChip key={`${m.gw}-${m.opponent}`} m={m} />
                    ))}
                </div>
            </div>

            {g.availability?.level && g.availability.level !== "available" && (
                <div className="border border-fa-red/40 bg-fa-red/10 px-3 py-2 flex items-center gap-2 text-fa-red font-mono text-xs">
                    <Warning size={14} weight="fill" />
                    {g.availability.label} — {g.availability.news || "unavailable"}
                </div>
            )}

            <div className="font-mono text-[10px] uppercase tracking-widest text-white/40 flex items-center gap-1">
                <Info size={10} weight="bold" />
                {g.total_points}pts · {g.minutes}min · {g.starts} starts this season
            </div>
        </div>
    );
}

function MeterBar({ label, value, weight }) {
    const pct = Math.round(value * 100);
    return (
        <div>
            <div className="font-mono text-[9px] uppercase tracking-widest text-white/50 flex items-center justify-between mb-1">
                <span>{label}</span>
                <span className="text-white/30">×{Math.round(weight * 100)}%</span>
            </div>
            <div className="h-1 bg-white/5 relative">
                <div
                    className="absolute top-0 left-0 h-1 bg-fa-yellow"
                    style={{ width: `${pct}%` }}
                />
            </div>
            <div className="font-mono text-[10px] text-fa-yellow mt-1 tabular-nums">{pct}%</div>
        </div>
    );
}

function MatchupChip({ m }) {
    const color = MATCHUP_COLOR[m.label] || "#A1A1AA";
    const confStyle = CONFIDENCE_STYLE[m.confidence] || CONFIDENCE_STYLE.LOW;
    const fdrColor = FDR_COLOR[m.fdr] || "#888";
    return (
        <div
            className="bg-black border px-2.5 py-1.5 flex items-center gap-2"
            style={{ borderColor: `${color}60` }}
            data-testid={`matchup-chip-${m.opponent}`}
        >
            <span className="font-mono text-[11px]">
                GW{m.gw} {m.home_away === "H" ? "vs" : "@"} <span className="font-display font-bold">{m.opponent}</span>
            </span>
            {m.matchup_delta != null ? (
                <span
                    className="font-mono text-[11px] font-bold tabular-nums"
                    style={{ color }}
                >
                    Δ{m.matchup_delta >= 0 ? "+" : ""}{m.matchup_delta}
                </span>
            ) : (
                <span className="font-mono text-[10px] text-white/40">—</span>
            )}
            {m.matches > 0 && (
                <span className="font-mono text-[9px] text-white/50">{m.matches}m</span>
            )}
            <span
                className="font-tag text-[9px] font-bold uppercase px-1 py-0.5 leading-none"
                style={{ background: confStyle.bg, color: confStyle.text }}
                title={`Confidence based on sample size. ${m.matches} appearances.`}
            >
                {m.confidence === "LOW_SAMPLE" ? "LOW" : m.confidence}
            </span>
            <span
                className="font-tag text-[9px] font-bold uppercase px-1 py-0.5 leading-none"
                style={{ background: `${fdrColor}20`, color: fdrColor, border: `1px solid ${fdrColor}60` }}
                title={`Fixture Difficulty Rating`}
            >
                FDR {m.fdr != null ? m.fdr : "?"}
            </span>
        </div>
    );
}
